// ...existing code...
import React, { useEffect, useState } from 'react';
import { Container, Typography, Box, Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Alert, Snackbar, CircularProgress } from '@mui/material';
import axios from '../api/axios';

interface OrderBatch {
  batchId: number;
  isOpen: boolean;
  orderDate: string;
  deliveryDate: string;
  taxRate: number;
  shipping: number;
}

const AdminMenu: React.FC = () => {
  const [orderBatch, setOrderBatch] = useState<OrderBatch | null>(null);
  const [loading, setLoading] = useState(true);

  // State for Add/Edit Order modal
  const [orderModalOpen, setOrderModalOpen] = useState(false);
  const [users, setUsers] = useState<any[]>([]);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [orderFormUserId, setOrderFormUserId] = useState<string | null>(null);

  // Handler to open Add/Edit Order modal and fetch users
  const handleOpenOrderModal = async () => {
    setOrderModalOpen(true);
    setOrderFormUserId(null);
    setSelectedUserId(null);
    try {
      const res = await axios.get('/user');
      setUsers(res.data || []);
    } catch {
      setUsers([]);
    }
  };
  // State for Open Ordering dialog
  const [openOrderingDialog, setOpenOrderingDialog] = useState(false);
  const [orderDate, setOrderDate] = useState('');
  const [deliveryDate, setDeliveryDate] = useState('');
  const [taxRate, setTaxRate] = useState(0.06);
  const [shipping, setShipping] = useState(0.35);

  const [orderingError, setOrderingError] = useState('');
  // State for Close Ordering dialog
  const [closeOrderingDialog, setCloseOrderingDialog] = useState(false);

  // State for Update Shipping/Tax/Delivery dialog
  const [updateDialogOpen, setUpdateDialogOpen] = useState(false);
  const [updateOrderDate, setUpdateOrderDate] = useState('');
  const [updateDeliveryDate, setUpdateDeliveryDate] = useState('');
  const [updateTaxRate, setUpdateTaxRate] = useState(0.06);
  const [updateShipping, setUpdateShipping] = useState(0.35);
  const [updateError, setUpdateError] = useState('');

  // State for Print Customer Receipts dialog
  const [printDialogOpen, setPrintDialogOpen] = useState(false);
  const [printLoading, setPrintLoading] = useState(false);
  const [orderDetails, setOrderDetails] = useState<any[]>([]);
  const [batchDetails, setBatchDetails] = useState<OrderBatch | null>(null);
  const [printError, setPrintError] = useState('');

  // State for Email Customer Receipts dialog
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [emailUserCount, setEmailUserCount] = useState<number | null>(null);
  const [emailSending, setEmailSending] = useState(false);
  const [emailError, setEmailError] = useState<string | null>(null);
  // Snackbar for success toast
  const [emailToastOpen, setEmailToastOpen] = useState(false);
  const [emailToastMessage, setEmailToastMessage] = useState('');

  useEffect(() => {
    axios.get('/orderbatch/current')
      .then((response) => {
        setOrderBatch(response.data);
      })
      .catch(() => {
        setOrderBatch(null);
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  const hasBatch = !!(orderBatch && orderBatch.isOpen);
  
  // Helper to reopen last order batch
  const handleReopenLastOrder = async () => {
    try {
      // Get the most recent batch
      const response = await axios.get('/orderbatch/current');
      const batch = response.data;
      if (!batch || !batch.batchId) return;
      // Mark as open
      await axios.put(`/orderBatch/${batch.batchId}`, { ...batch, isOpen: true });
      // Refetch current batch to update UI
      const current = await axios.get('/orderbatch/current');
      setOrderBatch(current.data);
    } catch (err) {
      // Optionally show error
    }
  };

  const printRef = React.useRef<HTMLDivElement>(null);

  // Helper to handle printing the modal contents
  const handlePrint = () => {
    if (!printRef.current) return;
    const printContents = printRef.current.innerHTML;
    const printWindow = window.open('', '', 'height=800,width=1000');
    if (printWindow) {
      printWindow.document.write('<html><head><title>Customer Receipts</title>');
      printWindow.document.write('<style>body{font-family:sans-serif;} table{border-collapse:collapse;width:100%;} th,td{border:1px solid #e0e0e0;padding:8px;} th{background:#f5f7fa;} .print-page-break{page-break-after: always !important; break-after: page !important;}</style>');
      printWindow.document.write('</head><body>');
      printWindow.document.write(printContents);
      printWindow.document.write('</body></html>');
      printWindow.document.close();
      printWindow.focus();
      printWindow.print();
      // Optionally close after print
      // printWindow.close();
    }
  };

  // Helper to export as CSV
  const handleExportCSV = () => {
    if (!orderDetails.length) return;
    // Group orderDetails by userId
    const grouped: { [userId: string]: any[] } = {};
    orderDetails.forEach((item: any) => {
      if (!grouped[item.userId]) grouped[item.userId] = [];
      grouped[item.userId].push(item);
    });
    let csv = 'Customer,Email,Phone,SKU,Name,Weight (lbs),Qty,Price,Sub-Total,Tax,Shipping,Grand Total\n';
    Object.entries(grouped).forEach(([userId, items]) => {
      const user = (items as any[])[0];
      const fullName = `${user.lastName}, ${user.firstName}`;
      const email = user.email || '';
      const phone = user.phone || '';
      (items as any[]).forEach((item) => {
        const subTotal = item.price * item.quantity;
        const tax = (orderBatch?.taxRate || 0) * subTotal;
        const shipping = (orderBatch?.shipping || 0) * (item.weight || 0) * item.quantity;
        const grandTotal = subTotal + tax + shipping;
        csv += `"${fullName}","${email}","${phone}","${item.sku}","${item.catalogName}",${Number(item.weight * item.quantity).toFixed(2)},${item.quantity},${Number(item.price).toFixed(2)},${subTotal.toFixed(2)},${tax.toFixed(2)},${shipping.toFixed(2)},${grandTotal.toFixed(2)}\n`;
      });
    });
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'customer_receipts.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  return (
    <Container maxWidth="sm" sx={{ py: 4 }}>
      <Typography variant="h5" align="center" sx={{ fontWeight: 700, mb: 3, color: 'primary.main' }}>
        Admin Menu
      </Typography>
      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
          <CircularProgress size={56} color="primary" thickness={4.5} />
        </Box>
      ) : (
        <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
          <Button variant="contained" color="primary" sx={{ width: 260 }} onClick={handleOpenOrderModal}>
            Add/Edit Order
          </Button>
      {/* Add/Edit Order Modal */}
      <Dialog open={orderModalOpen} onClose={() => setOrderModalOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ bgcolor: '#fff' }}>Add/Edit Order for User</DialogTitle>
        <DialogContent sx={{ bgcolor: '#fff' }}>
          <Box sx={{ mb: 2 }}>
            <Typography>Select a user to add or edit their order:</Typography>
            <select
              style={{ width: '100%', padding: 8, fontSize: 16, marginTop: 8, marginBottom: 16 }}
              value={selectedUserId ?? ''}
              onChange={e => setSelectedUserId(e.target.value)}
            >
              <option value="" disabled>Select user...</option>
              {users.map(u => (
                <option key={u.userId} value={u.userId}>
                  {u.fName} {u.lName} ({u.email})
                </option>
              ))}
            </select>
            <Button
              variant="contained"
              color="primary"
              disabled={!selectedUserId}
              onClick={() => setOrderFormUserId(selectedUserId)}
            >
              Edit Order
            </Button>
          </Box>
          {orderFormUserId && (
            <Box sx={{ border: '1px solid #eee', borderRadius: 2, p: 2, mt: 2, bgcolor: '#f9f9f9' }}>
              {/* Render OrderForm for selected user */}
              {React.createElement(require('./OrderForm').default, { userId: orderFormUserId })}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ bgcolor: '#fff' }}>
          <Button onClick={() => setOrderModalOpen(false)} color="inherit">Close</Button>
        </DialogActions>
      </Dialog>
          {/* ...existing code... */}
          { !hasBatch && (
            <>
              <Button
                variant="contained"
                color="primary"
                sx={{ width: 260 }}
                onClick={() => {
                  // Set order date to one week from today
                  const today = new Date();
                  const oneWeekOut = new Date(today.getTime() + 7 * 24 * 60 * 60 * 1000);
                  const orderDateStr = oneWeekOut.toISOString().slice(0, 10);
                  // Delivery date is exactly one week after order date
                  const deliveryDateObj = new Date(oneWeekOut.getTime() + 7 * 24 * 60 * 60 * 1000);
                  const deliveryDateStr = deliveryDateObj.toISOString().slice(0, 10);
                  setOrderDate(orderDateStr);
                  setDeliveryDate(deliveryDateStr);
                  setTaxRate(0.06);
                  setShipping(0.35);
                  setOpenOrderingDialog(true);
                }}
              >
                Open New Order
              </Button>
              <Button
                variant="contained"
                color="secondary"
                sx={{ width: 260 }}
                onClick={handleReopenLastOrder}
              >
                Reopen Last Order
              </Button>
            </>
          )}
          <Button variant="contained" color="primary" sx={{ width: 260 }} component={require('react-router-dom').Link} to="/edit-catalog">
            Edit Catalog
          </Button>
          { hasBatch && (
            <Button
              variant="contained"
              color="primary"
              sx={{ width: 260 }}
              onClick={() => setCloseOrderingDialog(true)}
            >
              Close Ordering
            </Button>
          )}
            <Button variant="contained" color="primary" sx={{ width: 260 }} component={require('react-router-dom').Link} to="/consolidated-order">
              Consolidated Order Form
            </Button>
        <Button
          variant="contained"
          color="primary"
          sx={{ width: 260 }}
          onClick={() => {
            if (orderBatch) {
              // Ensure ISO date format (yyyy-mm-dd) for input fields
              const orderDateStr = orderBatch.orderDate ? orderBatch.orderDate.slice(0, 10) : '';
              const deliveryDateStr = orderBatch.deliveryDate ? orderBatch.deliveryDate.slice(0, 10) : '';
              setUpdateOrderDate(orderDateStr);
              setUpdateDeliveryDate(deliveryDateStr);
              setUpdateTaxRate(orderBatch.taxRate ?? 0.06);
              setUpdateShipping(orderBatch.shipping ?? 0.35);
              setUpdateError('');
              setUpdateDialogOpen(true);
            }
          }}
        >
          Update Shipping & Tax
        </Button>
        <Button
          variant="contained"
          color="primary"
          sx={{ width: 260 }}
          onClick={async () => {
            setPrintDialogOpen(true);
            setPrintLoading(true);
            setPrintError('');
            try {
              // Fetch order details and batch details in parallel
              const batchId = orderBatch?.batchId;
              const [detailRes, batchRes] = await Promise.all([
                axios.get(`/order/details${batchId ? `?batchId=${batchId}` : ''}`),
                axios.get('/orderbatch/current')
              ]);
              setOrderDetails(detailRes.data || []);
              setBatchDetails(batchRes.data || null);
            } catch (err) {
              setPrintError('Failed to load order details.');
            } finally {
              setPrintLoading(false);
            }
          }}
        >
          Print Customer Receipts
        </Button>
      {/* Print Customer Receipts Dialog */}
      <Dialog open={printDialogOpen} onClose={() => setPrintDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ bgcolor: '#fff' }}>Customer Receipts</DialogTitle>
        <DialogContent dividers sx={{ bgcolor: '#fff' }}>
          <div ref={printRef}>
            {printLoading ? (
              <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
                <CircularProgress size={48} color="primary" />
              </Box>
            ) : printError ? (
              <Alert severity="error">{printError}</Alert>
            ) : (
              <Box>
                {orderDetails.length === 0 && (
                  <Typography color="text.secondary" align="center">No order details found.</Typography>
                )}
                {(() => {
                  // Group orderDetails by userId
                  const grouped: { [userId: string]: any[] } = {};
                  orderDetails.forEach((item: any) => {
                    if (!grouped[item.userId]) grouped[item.userId] = [];
                    grouped[item.userId].push(item);
                  });
                  return Object.entries(grouped).map(([userId, items]) => {
                    if (!(items as any[]).length) return null;
                    const user = (items as any[])[0];
                    const fullName = `${user.lastName}, ${user.firstName}`;
                    let phone = user.phone || '';
                    const email = user.email || '';
                    // Format phone as (123) 456-7890 if 10 digits
                    if (phone && typeof phone === 'string') {
                      const digits = phone.replace(/\D/g, '');
                      if (digits.length === 10) {
                        phone = `(${digits.slice(0,3)}) ${digits.slice(3,6)}-${digits.slice(6)}`;
                      }
                    }
                    const subtotal = (items as any[]).reduce((sum: number, i: any) => sum + i.price * i.quantity, 0);
                    const totalWeight = (items as any[]).reduce((sum: number, i: any) => sum + (i.weight * i.quantity), 0);
                    const taxRate = batchDetails?.taxRate ?? 0.06;
                    const shippingRate = batchDetails?.shipping ?? 0.35;
                    const tax = subtotal * taxRate;
                    const shipping = shippingRate * totalWeight;
                    const grandTotal = subtotal + tax + shipping;
                    return (
                      <Box key={userId} mb={4} p={2} border={1} borderColor="#e0e0e0" borderRadius={2} bgcolor="#fff" className="print-page-break">
                        <Box display="flex" alignItems="center" mb={1} flexWrap="wrap" gap={2}>
                          <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.main', mb: 0 }}>{fullName}</Typography>
                          {phone && (
                            <Typography variant="body1" sx={{ color: 'text.secondary', fontSize: 15 }}>{phone}</Typography>
                          )}
                          {email && (
                            <Typography variant="body1" sx={{ color: 'text.secondary', fontSize: 15 }}>{email}</Typography>
                          )}
                        </Box>
                        <table style={{ width: '100%', marginBottom: 8, borderCollapse: 'collapse' }}>
                          <thead>
                            <tr style={{ background: '#f5f7fa' }}>
                              <th style={{ padding: 8, fontWeight: 700, fontSize: 15, border: '1px solid #e0e0e0', textAlign: 'left' }}>SKU</th>
                              <th style={{ padding: 8, fontWeight: 700, fontSize: 15, border: '1px solid #e0e0e0', textAlign: 'left' }}>Name</th>
                              <th style={{ padding: 8, fontWeight: 700, fontSize: 15, border: '1px solid #e0e0e0', textAlign: 'center' }}>Weight (lbs)</th>
                              <th style={{ padding: 8, fontWeight: 700, fontSize: 15, border: '1px solid #e0e0e0', textAlign: 'center' }}>Qty</th>
                              <th style={{ padding: 8, fontWeight: 700, fontSize: 15, border: '1px solid #e0e0e0', textAlign: 'right' }}>Price</th>
                              <th style={{ padding: 8, fontWeight: 700, fontSize: 15, border: '1px solid #e0e0e0', textAlign: 'right' }}>Total Price</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(items as any[]).map((item: any, iidx: number) => (
                              <tr key={iidx}>
                                <td style={{ padding: 8, border: '1px solid #e0e0e0' }}>{item.sku}</td>
                                <td style={{ padding: 8, border: '1px solid #e0e0e0' }}>{item.catalogName}</td>
                                <td style={{ padding: 8, border: '1px solid #e0e0e0', textAlign: 'center' }}>{Number(item.weight).toFixed(2)}</td>
                                <td style={{ padding: 8, border: '1px solid #e0e0e0', textAlign: 'center' }}>{item.quantity}</td>
                                <td style={{ padding: 8, border: '1px solid #e0e0e0', textAlign: 'right' }}>{`$${Number(item.price).toFixed(2)}`}</td>
                                <td style={{ padding: 8, border: '1px solid #e0e0e0', textAlign: 'right' }}>{`$${(item.price * item.quantity).toFixed(2)}`}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        <Box display="flex" flexDirection="column" alignItems="flex-end" mt={2}>
                          <Typography sx={{ fontWeight: 600 }}>Subtotal: ${subtotal.toFixed(2)}</Typography>
                          <Typography sx={{ fontWeight: 600 }}>Tax ({(taxRate * 100).toFixed(2)}%): ${tax.toFixed(2)}</Typography>
                          <Typography sx={{ fontWeight: 600 }}>Total Weight: {totalWeight.toFixed(2)} lbs</Typography>
                          <Typography sx={{ fontWeight: 600 }}>Shipping: ${shipping.toFixed(2)}</Typography>
                          <Typography sx={{ fontWeight: 700, color: 'primary.main', fontSize: 18 }}>Grand Total: ${grandTotal.toFixed(2)}</Typography>
                        </Box>
                      </Box>
                    );
                  });
                })()}
              </Box>
            )}
          </div>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'space-between', px: 3, pb: 2, bgcolor: '#fff' }}>
          <Box>
            <Button onClick={handlePrint} color="primary" variant="outlined" sx={{ mr: 1 }}>Print</Button>
            <Button onClick={handleExportCSV} color="primary" variant="outlined">Export CSV</Button>
          </Box>
          <Button onClick={() => setPrintDialogOpen(false)} color="primary" variant="contained">Close</Button>
        </DialogActions>
      </Dialog>
        <Button variant="contained" color="primary" sx={{ width: 260 }} onClick={async () => {
          setEmailDialogOpen(true);
          setEmailError(null);
          setEmailUserCount(null);
          try {
            const batchId = orderBatch?.batchId;
            if (!batchId) return;
            const res = await axios.get(`/order/details?batchId=${batchId}`);
            const userIds = new Set((res.data || []).map((item: any) => item.userId));
            setEmailUserCount(userIds.size);
          } catch {
            setEmailError('Failed to load order details.');
          }
        }}>E-Mail Customer Receipts</Button>
        {/* Customer Checklist button removed */}
        </Box>
      )}

      {/* Update Shipping/Tax/Delivery Dialog */}
      <Dialog open={updateDialogOpen} onClose={() => setUpdateDialogOpen(false)}>
        <DialogTitle sx={{ bgcolor: '#fff' }}>Update Shipping, Tax & Delivery Dates</DialogTitle>
        <DialogContent sx={{ bgcolor: '#fff' }}>
          <TextField
            margin="dense"
            label="Order Date"
            type="date"
            fullWidth
            InputLabelProps={{ shrink: true }}
            value={updateOrderDate}
            onChange={e => setUpdateOrderDate(e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Delivery Date"
            type="date"
            fullWidth
            InputLabelProps={{ shrink: true }}
            value={updateDeliveryDate}
            onChange={e => setUpdateDeliveryDate(e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Tax Rate"
            type="number"
            fullWidth
            inputProps={{ min: 0, step: 0.01 }}
            value={updateTaxRate}
            onChange={e => setUpdateTaxRate(Number(e.target.value))}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Shipping"
            type="number"
            fullWidth
            inputProps={{ min: 0, step: 0.01 }}
            value={updateShipping}
            onChange={e => setUpdateShipping(Number(e.target.value))}
          />
          {updateError && (
            <Alert severity="error" sx={{ mt: 2 }}>{updateError}</Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ bgcolor: '#fff' }}>
          <Button onClick={() => setUpdateDialogOpen(false)} color="inherit">Cancel</Button>
          <Button
            color="primary"
            variant="contained"
            onClick={async () => {
              if (!orderBatch) return;
              if (!updateOrderDate || !updateDeliveryDate) {
                setUpdateError('Order Date and Delivery Date are required.');
                return;
              }
              setUpdateError('');
              try {
                await axios.put(`/orderBatch/${orderBatch.batchId}`, {
                  ...orderBatch,
                  orderDate: updateOrderDate,
                  deliveryDate: updateDeliveryDate,
                  taxRate: updateTaxRate,
                  shipping: updateShipping
                });
                setUpdateDialogOpen(false);
                // Refetch order batch to update UI
                const response = await axios.get('/orderbatch/current');
                setOrderBatch(response.data);
              } catch {
                setUpdateError('Failed to update order batch.');
              }
            }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
      {/* Open Ordering Dialog */}
      {/* Close Ordering Dialog */}
      <Dialog open={closeOrderingDialog} onClose={() => setCloseOrderingDialog(false)}>
        <DialogTitle sx={{ bgcolor: '#fff' }}>Close Ordering</DialogTitle>
        <DialogContent sx={{ bgcolor: '#fff' }}>
          <Typography gutterBottom>
            Are you sure you want to close the current order batch?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ bgcolor: '#fff' }}>
          <Button onClick={() => setCloseOrderingDialog(false)} color="inherit">Cancel</Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => {
              if (!orderBatch) return;
              // Call OrderBatchController.Update with isOpen=false and current batch values
              axios.put(`/orderBatch/${orderBatch.batchId}`, {
                ...orderBatch,
                batchId: orderBatch.batchId,
                isOpen: false
              })
                .then(() => {
                  setCloseOrderingDialog(false);
                  // Refetch order batch to update UI
                  axios.get('/orderbatch/current')
                    .then((response) => {
                      setOrderBatch(response.data);
                    })
                    .catch(() => {
                      setOrderBatch(null);
                    });
                })
                .catch(() => {
                  setCloseOrderingDialog(false);
                  // Optionally show error
                });
            }}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={openOrderingDialog} onClose={() => setOpenOrderingDialog(false)}>
        <DialogTitle sx={{ bgcolor: '#fff' }}>Open Ordering</DialogTitle>
        <DialogContent sx={{ bgcolor: '#fff' }}>
          <TextField
            margin="dense"
            label="Order Date"
            type="date"
            fullWidth
            InputLabelProps={{ shrink: true }}
            value={orderDate}
            onChange={e => setOrderDate(e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Delivery Date"
            type="date"
            fullWidth
            InputLabelProps={{ shrink: true }}
            value={deliveryDate}
            onChange={e => setDeliveryDate(e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Tax Rate"
            type="number"
            fullWidth
            inputProps={{ min: 0, step: 0.01 }}
            value={taxRate}
            onChange={e => setTaxRate(Number(e.target.value))}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Shipping"
            type="number"
            fullWidth
            inputProps={{ min: 0, step: 0.01 }}
            value={shipping}
            onChange={e => setShipping(Number(e.target.value))}
          />
          {orderingError && (
            <Alert severity="error" sx={{ mt: 2 }}>{orderingError}</Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ bgcolor: '#fff' }}>
          <Button onClick={() => setOpenOrderingDialog(false)} color="inherit">Cancel</Button>
          <Button
            color="primary"
            variant="contained"
            onClick={() => {
              if (!orderDate || !deliveryDate) {
                setOrderingError('Order Date and Delivery Date are required.');
                return;
              }
              setOrderingError('');
              // Call OrderBatchController.Create
              const batch: OrderBatch = {
                isOpen: true,
                batchId: 0,
                orderDate,
                deliveryDate,
                taxRate,
                shipping
              };
              axios.post('/orderBatch', batch)
                .then((response) => {
                  setOpenOrderingDialog(false);
                  // Use batchId from server response if available
                  const returnedBatch = response.data && response.data.batchId ? { ...batch, batchId: response.data.batchId } : batch;
                  setOrderBatch(returnedBatch);
                })
                .catch(() => {
                  setOrderingError('Failed to open ordering.');
                });
            }}
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>

      {/* Email Customer Receipts Dialog */}
      <Dialog open={emailDialogOpen} onClose={() => setEmailDialogOpen(false)}>
        <DialogTitle sx={{ bgcolor: '#fff' }}>Email Customer Receipts</DialogTitle>
        <DialogContent sx={{ bgcolor: '#fff' }}>
          {emailError && <Alert severity="error" sx={{ mb: 2 }}>{emailError}</Alert>}
          {emailUserCount === null && !emailError ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight={80}>
              <CircularProgress size={32} color="primary" />
            </Box>
          ) : (
            <Typography>
              Are you sure you want to send receipts to <b>{emailUserCount}</b> user{emailUserCount === 1 ? '' : 's'}?
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ bgcolor: '#fff' }}>
          <Button onClick={() => setEmailDialogOpen(false)} color="inherit" disabled={emailSending}>
            Cancel
          </Button>
          <Button
            color="primary"
            variant="contained"
            disabled={emailSending || emailUserCount === null || !!emailError}
            onClick={async () => {
              setEmailSending(true);
              setEmailError(null);
              try {
                const batchId = orderBatch?.batchId;
                await axios.post(`/order/email?batchId=${batchId}`);
                // Close the dialog and show a toast notification
                setEmailDialogOpen(false);
                setEmailToastMessage('Emails Sent Successfully');
                setEmailToastOpen(true);
              } catch {
                setEmailError('Failed to send emails.');
              } finally {
                setEmailSending(false);
              }
            }}
          >
            {emailSending ? <CircularProgress size={20} color="inherit" /> : 'Send Emails'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar toast for email success */}
      <Snackbar
        open={emailToastOpen}
        autoHideDuration={4000}
        onClose={() => setEmailToastOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setEmailToastOpen(false)} severity="success" sx={{ width: '100%' }}>
          {emailToastMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default AdminMenu;
